import { ChatMessage } from '../types/chat';
import { marked } from 'marked';

export const exportMessageToPDF = async (message: ChatMessage, sessionTitle?: string) => {
  // Create a new window for the PDF content
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow pop-ups to export PDF');
    return;
  }

  // Convert markdown to HTML
  const markdownContent = convertCustomTableToMarkdown(message.content);
  const htmlFromMarkdown = await marked.parse(markdownContent, { gfm: true });

  // Create simple HTML content with just the message text
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Message</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
          font-size: 16px;
        }

        .message-content {
          word-wrap: break-word;
        }

        table {
          border-collapse: collapse;
          width: 100%;
          margin: 20px 0;
        }

        th, td {
          border: 1px solid #ddd;
          padding: 12px;
          text-align: left;
        }

        th {
          background-color: #f2f2f2;
          font-weight: 600;
        }

        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        code {
          background-color: #f4f4f4;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
        }

        pre {
          background-color: #f4f4f4;
          padding: 15px;
          border-radius: 5px;
          overflow-x: auto;
        }

        pre code {
          background-color: transparent;
          padding: 0;
        }

        blockquote {
          border-left: 4px solid #ddd;
          margin: 0;
          padding-left: 20px;
          color: #666;
        }

        h1, h2, h3, h4, h5, h6 {
          margin-top: 24px;
          margin-bottom: 16px;
          font-weight: 600;
        }

        ul, ol {
          padding-left: 30px;
        }

        @media print {
          body {
            padding: 20px;
          }
        }

        @page {
          margin: 1in;
          size: letter;
        }
      </style>
    </head>
    <body>
      <div class="message-content">
        ${htmlFromMarkdown}
      </div>
    </body>
    </html>
  `;

  // Write content and trigger print
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  // Wait for content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      // Close the window after printing (user can cancel)
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };
};

// Helper function to convert custom [TABLE] tags to markdown
const convertCustomTableToMarkdown = (content: string): string => {
  return content.replace(/\[TABLE\]([\s\S]*?)\[\/TABLE\]/g, (match, tableContent) => {
    const lines = tableContent.trim().split('\n').filter((line: string) => line.trim());
    if (lines.length === 0) return match;

    const headerLine = lines[0];
    const headers = headerLine.split('|').map((h: string) => h.trim());

    let markdownTable = '\n| ' + headers.join(' | ') + ' |\n';
    markdownTable += '| ' + headers.map(() => '---').join(' | ') + ' |\n';

    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split('|').map((c: string) => c.trim());
      markdownTable += '| ' + cells.join(' | ') + ' |\n';
    }

    return markdownTable;
  });
};

// Helper function to format file size
const formatFileSize = (bytes: number | undefined | null): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Export multiple messages
export const exportMessagesToPDF = async (messages: ChatMessage[], sessionTitle?: string) => {
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow pop-ups to export PDF');
    return;
  }

  const title = sessionTitle || 'Chat Conversation';

  // Convert all message contents to HTML
  const messagePromises = messages.map(async (message) => {
    const markdownContent = convertCustomTableToMarkdown(message.content);
    const htmlFromMarkdown = await marked.parse(markdownContent, { gfm: true });
    const messageType = message.message_type === 'user' ? 'User' : 'AI Assistant';
    const timestamp = new Date(message.created_at).toLocaleString();

    return `
      <div class="message ${message.message_type}">
        <div class="message-header">
          <span class="message-type ${message.message_type === 'user' ? 'user-type' : 'assistant-type'}">
            ${messageType}
          </span>
          <span class="message-timestamp">
            ${timestamp}
          </span>
        </div>

        <div class="message-content">
          ${htmlFromMarkdown}
        </div>

        ${message.attachments && message.attachments.length > 0 ? `
          <div class="attachments">
            <div class="attachments-title">Attachments:</div>
            ${message.attachments.map(attachment => {
              const fileName = attachment.document_name || attachment.filename || 'Unknown file';
              const fileSize = attachment.document_size || attachment.file_size || 0;
              return `
              <div class="attachment-item">
                📎 ${fileName} (${formatFileSize(fileSize)})
              </div>
            `;
            }).join('')}
          </div>
        ` : ''}
      </div>
    `;
  });

  const messagesHtml = (await Promise.all(messagePromises)).join('');

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${title}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .header {
          text-align: center;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e9ecef;
        }

        .title {
          font-size: 24px;
          font-weight: 600;
          margin: 0 0 10px 0;
          color: #333;
        }

        .subtitle {
          font-size: 16px;
          color: #666;
          margin: 0;
        }

        .message {
          margin-bottom: 30px;
          page-break-inside: avoid;
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .message-type {
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
        }

        .user-type {
          background: #667eea;
          color: white;
        }

        .assistant-type {
          background: #e9ecef;
          color: #333;
        }

        .message-timestamp {
          color: #666;
          font-size: 12px;
        }

        .message-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
          word-wrap: break-word;
        }

        .message.user .message-content {
          background: #f8f9ff;
          border-color: #667eea;
        }

        table {
          border-collapse: collapse;
          width: 100%;
          margin: 20px 0;
        }

        th, td {
          border: 1px solid #ddd;
          padding: 12px;
          text-align: left;
        }

        th {
          background-color: #f2f2f2;
          font-weight: 600;
        }

        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        code {
          background-color: #f4f4f4;
          padding: 2px 6px;
          border-radius: 3px;
          font-family: 'Courier New', monospace;
        }

        pre {
          background-color: #f4f4f4;
          padding: 15px;
          border-radius: 5px;
          overflow-x: auto;
        }

        pre code {
          background-color: transparent;
          padding: 0;
        }

        blockquote {
          border-left: 4px solid #ddd;
          margin: 0;
          padding-left: 20px;
          color: #666;
        }

        h1, h2, h3, h4, h5, h6 {
          margin-top: 24px;
          margin-bottom: 16px;
          font-weight: 600;
        }

        ul, ol {
          padding-left: 30px;
        }

        .attachments {
          margin-top: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 6px;
        }

        .attachments-title {
          font-weight: 600;
          margin-bottom: 10px;
          color: #333;
          font-size: 14px;
        }

        .attachment-item {
          padding: 5px 0;
          font-size: 12px;
          color: #666;
        }

        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e9ecef;
          text-align: center;
          font-size: 12px;
          color: #666;
        }

        @media print {
          body {
            padding: 20px;
          }
        }

        @page {
          margin: 1in;
          size: letter;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="title">${title}</h1>
        <p class="subtitle">Complete Conversation Export</p>
      </div>

      ${messagesHtml}

      <div class="footer">
        <p>Exported from AI Chat System on ${new Date().toLocaleString()}</p>
        <p>Total Messages: ${messages.length}</p>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();

  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };
};